import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, DialogContentText, InputAdornment } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import {
  ValidatePasswordExpiryToken,
  SetPasswordFirstTime,
} from '../../services/auth';
import { useHistory } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { Container, Content, Logo, LogoDialog } from './styles';
import {
  CustomDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CustomeTextField,
  StyledDialogErrorTitle,
  EyeIcon,
} from './styles';
import logo from '../../assets/images/logo.png';
import { GetPasswordConfigurations } from '../../services/staff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import loadingIcon from '../../assets/images/loading.gif';

const PasswordSet = () => {
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast(false);
  const { search } = useLocation();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(false);

  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [newPasswordError, setNewPasswordError] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState('');
  const [passwordRules, setPasswordRules] = React.useState({}); //intl.formatMessage({ id: 'passwordRules' })
  const [passwordRulesString, setPasswordRulesString] = React.useState('');

  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const query = useMemo(() => {
    const queryObject = {};
    const params = search.slice(1).split('&');

    params.forEach((param) => {
      const [key, value] = param.split('=');
      if (key) {
        queryObject[decodeURIComponent(key)] = value
          ? decodeURIComponent(value)
          : '';
      }
    });
    return queryObject;
  }, [search]);

  const querytoken = query['token'];

  const logout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    //setPortal(null);
    history.push('/');
  }, [history]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await ValidatePasswordExpiryToken(querytoken);
        if (result.ValidatePasswordExpiryTokenResult) {
          setData(result);
          if (result.userId) {
            const passwordRulesResult = await GetPasswordConfigurations({
              identificationId: result.userId,
            });
            if (
              passwordRulesResult.GetPasswordConfigurationsResult &&
              passwordRulesResult.passwordConfigurations
            ) {
              const passwordConfigurations =
                passwordRulesResult.passwordConfigurations.reduce(
                  (acc, rule) => {
                    acc[rule.Key] = rule.Value;
                    return acc;
                  },
                  {}
                );

              if (passwordConfigurations) {
                setPasswordRules(passwordConfigurations);
              }
            }
          }
        } else {
          throw new Error('Sorry validity token could not be verified.');
        }
      } catch (err) {
        console.log(`${err.message}`);
        setError(true);
        addToast({
          title: intl.formatMessage({ id: 'PasswordTokenNotVerfiedError' }),
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [querytoken]);

  useEffect(() => {
    if (Object.keys(passwordRules).length > 0) {
      generatePasswordRuleString();
    }
  }, [passwordRules]);

  const generatePasswordRuleString = () => {
    const minPasswordChars = passwordRules['Minimum Password Characters'];
    const minUpperChars = passwordRules['Minimum Uppercase Characters'];
    const minLowerChars = passwordRules['Minimum Lowercase Characters'];
    const minSpecialChars = passwordRules['Minimum Special Characters'];
    const minNumbers = passwordRules['Minimum Numbers'];
    const specialChars = passwordRules['Special Characters'];

    const formattedPasswordRulesString = intl.formatMessage(
      {
        id: 'newPasswordRules',
      },
      {
        minPasswordChars,
        minUpperChars,
        minLowerChars,
        minSpecialChars,
        specialChars,
        minNumbers,
      }
    );
    setPasswordRulesString(formattedPasswordRulesString);
  };

  const validatePassword = (password) => {
    const minUpperChars = passwordRules['Minimum Uppercase Characters'];
    const minLowerChars = passwordRules['Minimum Lowercase Characters'];
    const minSpecialChars = passwordRules['Minimum Special Characters'];
    const minNumbers = passwordRules['Minimum Numbers'];

    const minLength = passwordRules['Minimum Password Characters']; //12;
    const uppercase = /[A-Z]/;
    const lowercase = /[a-z]/;
    const number = /[0-9]/;
    const specialChar = /[^A-Za-z0-9\s]/;
    const invalidChar = /[\\/]/;

    if (password) {
      if (password.length < minLength) {
        return intl.formatMessage({ id: 'PasswordTooShort' });
      }
      if (minUpperChars > 0 && !uppercase.test(password)) {
        return intl.formatMessage({ id: 'PasswordUppercaseRequired' });
      }
      if (minLowerChars > 0 && !lowercase.test(password)) {
        return intl.formatMessage({ id: 'PasswordLowercaseRequired' });
      }
      if (minNumbers > 0 && !number.test(password)) {
        return intl.formatMessage({ id: 'PasswordNumberRequired' });
      }
      if (minSpecialChars > 0 && !specialChar.test(password)) {
        return intl.formatMessage({ id: 'PasswordSpecialCharRequired' });
      }
      if (invalidChar.test(password)) {
        return intl.formatMessage({ id: 'PasswordInvalidChar' });
      }

      const uppercaseletters = /[A-Z]/g;
      const upperCaseCount = (password.match(uppercaseletters) || []).length;
      if (upperCaseCount < minUpperChars) {
        return intl.formatMessage({ id: 'PasswordUppercaseRequired' });
      }

      const lowercaseletters = /[a-z]/g;
      const lowerCaseCount = (password.match(lowercaseletters) || []).length;
      if (lowerCaseCount < minLowerChars) {
        return intl.formatMessage({ id: 'PasswordLowercaseRequired' });
      }

      const numberletters = /[0-9]/g;
      const minNumbersCount = (password.match(numberletters) || []).length;
      if (minNumbersCount < minNumbers) {
        return intl.formatMessage({ id: 'PasswordNumberRequired' });
      }

      const specialCharletters = /[^A-Za-z0-9\s]/g;
      const minSpecialCharsCount = (password.match(specialCharletters) || [])
        .length;
      if (minSpecialCharsCount < minSpecialChars) {
        return intl.formatMessage({ id: 'PasswordSpecialCharRequired' });
      }

      return '';
    } else {
      return intl.formatMessage({ id: 'NewPasswordRequired' });
    }
  };

  const handleSave = async () => {
    let hasError = false;

    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      setNewPasswordError(newPasswordError);
      hasError = true;
    } else {
      setNewPasswordError('');
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(
        intl.formatMessage({ id: 'PasswordsDoNotMatch' })
      );
      hasError = true;
    } else {
      setConfirmPasswordError('');
    }

    if (!hasError) {
      const result = await SetPasswordFirstTime({
        userId: data.userId,
        userName: data.userName,
        newPassword,
      });
      if (result.SetPasswordFirstTimeUserResult) {
        addToast({
          title: intl.formatMessage({ id: 'passwordUpdateNotification' }),
          type: 'success',
        });
        cancel();
        logout();
      } else {
        addToast({
          title: intl.formatMessage({ id: 'passwordUpdateError' }),
          type: 'error',
        });
      }
    }
  };

  const handleNewPasswordBlur = () => {
    const error = validatePassword(newPassword);
    setNewPasswordError(error);
  };

  const handleConfirmPasswordBlur = () => {
    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError(
        intl.formatMessage({ id: 'PasswordsDoNotMatch' })
      );
    } else {
      setConfirmPasswordError('');
    }
  };

  const cancel = () => {
    setNewPassword('');
    setConfirmPassword('');
    setNewPasswordError('');
    setConfirmPasswordError('');
    //handleCancel();
  };

  const handleTogglePasswordVisibility = () =>
    setShowNewPassword(!showNewPassword);

  const handleToggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  return (
    <Container>
      {loading ? (
        <Container>
          <Content>
            <Logo src={logo} alt="squre" />
            <img
              src={loadingIcon}
              alt="loading"
              style={{ height: 30, width: 30 }}
            />
          </Content>
        </Container>
      ) : !data ? (
        <Container>
          <Content>
            <Logo src={logo} alt="squre" />
            {error && (
              <StyledDialogErrorTitle>
                {intl.formatMessage({
                  id: 'SetPasswordTokenExpired',
                })}
              </StyledDialogErrorTitle>
            )}
          </Content>
        </Container>
      ) : (
        <Container>
          <Content>
            <LogoDialog src={logo} alt="squre" />
            <CustomDialog open={true} maxWidth="sm" fullWidth={true}>
              <StyledDialogTitle>
                {intl.formatMessage({ id: 'SetPassword' })}
              </StyledDialogTitle>
              <StyledDialogContent>
                <DialogContentText
                  dangerouslySetInnerHTML={{ __html: passwordRulesString }}
                />

                <CustomeTextField
                  id="new-password"
                  fullWidth
                  label={intl.formatMessage({ id: 'NewPassword' })}
                  value={newPassword}
                  color="secondary"
                  error={Boolean(newPasswordError)}
                  helperText={newPasswordError}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={handleNewPasswordBlur}
                  type={showNewPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <EyeIcon onClick={handleTogglePasswordVisibility}>
                          {showNewPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </EyeIcon>
                      </InputAdornment>
                    ),
                  }}
                />
                <CustomeTextField
                  id="confirm-password"
                  fullWidth
                  label={intl.formatMessage({ id: 'ConfirmNewPassword' })}
                  value={confirmPassword}
                  color="secondary"
                  error={Boolean(confirmPasswordError)}
                  helperText={confirmPasswordError}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={handleConfirmPasswordBlur}
                  type={showConfirmPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <EyeIcon
                          onClick={handleToggleConfirmPasswordVisibility}
                        >
                          {showConfirmPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </EyeIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledDialogContent>
              <StyledDialogActions>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleSave}
                >
                  {intl.formatMessage({ id: 'SetPassword' })}
                </Button>
              </StyledDialogActions>
            </CustomDialog>
          </Content>
        </Container>
      )}
    </Container>
  );
};

export default PasswordSet;
